import { Skeleton as MuiSkeleton } from '@mui/material';
import styled, { css, keyframes } from 'styled-components';

import { CONTRAST_FILL, CONTRAST_SHEEN, REVEAL_DELAY, REVEAL_DURATION } from './constants';
import { SkeletonRadius, SkeletonSize, SkeletonTextVariant, SkeletonTone } from './types';
import { getLength, getRadius } from './utils';

const reveal = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

interface GroupProps {
  $fill: boolean;
  $tone: SkeletonTone;
}

// A group stands in for block-level content, so it has to fill its parent the
// way that content would. Several pages centre their children in a flex
// column, where a plain block child shrinks to fit instead.
const Group = styled.div<GroupProps>`
  width: 100%;

  ${({ $fill }) =>
    $fill &&
    css`
      height: 100%;
    `}

  ${({ $tone }) =>
    $tone === 'contrast' &&
    css`
      --skeleton-fill: ${CONTRAST_FILL};
      --skeleton-sheen: ${CONTRAST_SHEEN};
    `}
`;

/**
 * Holds the skeleton's space from the first frame but keeps it invisible until
 * the delay elapses, so responses that beat it never flash a placeholder.
 */
const RevealGroup = styled(Group)`
  animation: ${reveal} ${REVEAL_DURATION} ease-out ${REVEAL_DELAY} both;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 1ms;
  }
`;

interface ShapeProps {
  $radius: SkeletonRadius;
  $width?: SkeletonSize;
  $height?: SkeletonSize;
  $grow: boolean;
}

const Shape = styled(MuiSkeleton)<ShapeProps>`
  && {
    border-radius: ${({ theme, $radius }) => getRadius(theme, $radius)};
    ${({ $width }) =>
      $width !== undefined &&
      css`
        width: ${getLength($width)};
      `}
    ${({ $height }) =>
      $height !== undefined &&
      css`
        height: ${getLength($height)};
      `}
    ${({ $grow, $width }) => {
      if ($grow) {
        return css`
          flex: 1 1 auto;
        `;
      }
      // A pixel width is a measurement of the element it stands in for, so it
      // must survive a flex parent intact.
      return (
        typeof $width === 'number' &&
        css`
          flex-shrink: 0;
        `
      );
    }}
  }
`;

interface TextShapeProps {
  $variant: SkeletonTextVariant;
}

/**
 * Sized against the typography scale so the placeholder occupies the same line
 * box the real text will, leaving nothing to reflow when it arrives.
 */
const TextShape = styled(Shape)<TextShapeProps>`
  && {
    font-size: ${({ theme, $variant }) => theme.typography[$variant].fontSize};
    line-height: ${({ theme, $variant }) => theme.typography[$variant].lineHeight};
  }
`;

const HiddenLabel = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
`;

export { Group, HiddenLabel, RevealGroup, Shape, TextShape };
